import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Card } from '../../../design-system/ui';
import { Link } from '../../../components/Link';
import type { JourneySurface, WireNudge } from '../../../types/journey';
import { usePlusEnabled } from '../plus/plus-flag';
import { fetchNext, reportActed, reportDismissed, type JourneyNextOptions } from './journey-api';
import { journeyMessages as m } from './journey-messages';

export interface UseJourneyNudge {
  nudge: WireNudge | null;
  loading: boolean;
  /** The person followed the nudge's link. */
  acted: () => void;
  /** "Not now": hides it and tells the server (30 days of snooze, the second one retires the rule). */
  dismiss: () => void;
}

/**
 * One nudge for a surface. Asked for once when the surface mounts (the server counts that as one
 * show and gives the same card back on a refresh inside the visit); renders nothing when the Plus
 * audience is closed, when signed out, or when the engine has nothing to say.
 */
export function useJourneyNudge(surface: JourneySurface, options: JourneyNextOptions = {}, enabled = true): UseJourneyNudge {
  const plusEnabled = usePlusEnabled();
  const [nudge, setNudge] = useState<WireNudge | null>(null);
  const [loading, setLoading] = useState(false);
  const asked = useRef<string | null>(null);
  const key = `${surface}|${options.jobId ?? ''}|${options.remote ?? ''}|${options.summer ?? ''}`;

  useEffect(() => {
    if (!plusEnabled || !enabled || asked.current === key) return;
    asked.current = key;
    let alive = true;
    setLoading(true);
    fetchNext(surface, options)
      .then((n) => alive && setNudge(n))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plusEnabled, enabled, key]);

  const acted = useCallback(() => {
    if (nudge) void reportActed(nudge);
  }, [nudge]);
  const dismiss = useCallback(() => {
    if (nudge) void reportDismissed(nudge);
    setNudge(null);
  }, [nudge]);
  return { nudge, loading, acted, dismiss };
}

/**
 * The "This week" card at the top of My Jobs (extends the Up next area): the single most useful
 * thing right now, with a button and "Not now". Reminders (an interview tomorrow, a follow-up
 * that is due, an offer waiting) always come before introductions of new features.
 */
export function ThisWeekCard() {
  const { nudge, acted, dismiss } = useJourneyNudge('my_jobs');
  return <ThisWeekCardView nudge={nudge} acted={acted} dismiss={dismiss} />;
}

/** The "This week" card body for a nudge someone already asked for (so a page asks the engine once). */
export function ThisWeekCardView({ nudge, acted, dismiss }: Pick<UseJourneyNudge, 'nudge' | 'acted' | 'dismiss'>) {
  const intl = useIntl();
  if (!nudge) return null;
  return (
    <section aria-labelledby="my-jobs-this-week" className="mb-3 md:mb-4" data-testid="journey-this-week" data-rule={nudge.ruleId}>
      <Card padding="md" className="flex flex-col gap-2">
        <h2 id="my-jobs-this-week" className="m-0 type-caption text-jc-ink-secondary">
          {intl.formatMessage(m.thisWeekHeading)}
        </h2>
        <p className="m-0 type-title text-jc-ink">{nudge.title}</p>
        <p className="m-0 type-body text-jc-ink-secondary">{nudge.body}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Link href={nudge.url} onClick={acted} className="inline-flex">
            <span className="type-body font-semibold text-jc-accent underline underline-offset-4">{nudge.cta}</span>
          </Link>
          <Button variant="tertiary" size="sm" className="ml-auto" onClick={dismiss}>
            {intl.formatMessage(m.notNow)}
          </Button>
        </div>
      </Card>
    </section>
  );
}

export interface JourneyGlanceRowProps {
  jobId: string;
  daysOpen?: number;
  finnishRequired?: boolean;
}

/** The job page's At a glance row: the stale ad check, the language check and any other job-page nudge. */
export function JourneyGlanceRow({ jobId, daysOpen, finnishRequired }: JourneyGlanceRowProps) {
  const intl = useIntl();
  const { nudge, acted, dismiss } = useJourneyNudge('job_page', { jobId, daysOpen, finnishRequired });
  if (!nudge) return null;
  return (
    <div className="flex flex-col gap-1 border-t border-jc-border pt-3" data-testid="journey-glance-row" data-rule={nudge.ruleId}>
      <p className="m-0 type-caption text-jc-ink-secondary">{intl.formatMessage(m.glanceLabel)}</p>
      <p className="m-0 type-body font-semibold text-jc-ink">{nudge.title}</p>
      <p className="m-0 type-body text-jc-ink-secondary">{nudge.body}</p>
      <div className="flex items-center gap-3">
        <Link href={nudge.url} onClick={acted} className="type-body font-semibold text-jc-accent underline underline-offset-4">
          {nudge.cta}
        </Link>
        {nudge.ruleId !== 'SRC-4' ? (
          <Button variant="tertiary" size="sm" onClick={dismiss}>
            {intl.formatMessage(m.notNow)}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** A compact nudge inside a page: the search results header, CV Studio, the account area. */
export function JourneyInline({ surface, options, className }: { surface: JourneySurface; options?: JourneyNextOptions; className?: string }) {
  const intl = useIntl();
  const { nudge, acted, dismiss } = useJourneyNudge(surface, options);
  if (!nudge) return null;
  return (
    <Card padding="md" className={`flex flex-col gap-1 ${className ?? ''}`} data-testid={`journey-inline-${surface}`} data-rule={nudge.ruleId}>
      <p className="m-0 type-body font-semibold text-jc-ink">{nudge.title}</p>
      <p className="m-0 type-body text-jc-ink-secondary">{nudge.body}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <Link href={nudge.url} onClick={acted} className="type-body font-semibold text-jc-accent underline underline-offset-4">
          {nudge.cta}
        </Link>
        <Button variant="tertiary" size="sm" className="ml-auto" onClick={dismiss}>
          {intl.formatMessage(m.notNow)}
        </Button>
      </div>
    </Card>
  );
}

/** Under the search results header: remote radar (SRC-2) and the summer job kit (SRC-5). Plus audience only; the server answers nothing to anyone signed out. */
export function SearchHeaderNudge({ remote, summer }: { remote: boolean; summer: boolean }) {
  if (!remote && !summer) return null;
  return <JourneyInline surface="search_header" options={{ remote, summer }} className="mb-2" />;
}
